import React from 'react';
import Select from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { BooleanReadView } from '@atlassian/jira-servicedesk-insight-attribute-views/src/ui/boolean/index.tsx';
import { selectInAModalStyleFixProps } from '@atlassian/jira-servicedesk-insight-shared-ui/src/common/constants/index.tsx';
import messages from './messages.tsx';
import type { BooleanOption } from './types.tsx';

const formatOptionLabel = ({ value }: { value: boolean | null }) => (
	<BooleanReadView value={value} />
);

type OnChangeValue =
	| {
			label: string;
			value: boolean | null;
	  }
	| null
	| undefined;

type Props = {
	value: boolean | null;
	validationState: 'default' | 'error';
	defaultMenuIsOpen?: boolean;
	autoFocus?: boolean;
	onChange: (option: BooleanOption) => void;
	isDisabled?: boolean;
	ariaDescribedBy?: string | undefined;
};

export const BooleanSelect = (props: Props) => {
	const { formatMessage } = useIntl();
	const { value, onChange, validationState, defaultMenuIsOpen, autoFocus } = props;

	return (
		<Select
			isDisabled={props.isDisabled}
			isSearchable={false}
			autoFocus={autoFocus}
			defaultMenuIsOpen={defaultMenuIsOpen}
			value={{
				// eslint-disable-next-line @atlassian/eng-health/code-evolution/ts-migration-4.9-5.4
				// @ts-expect-error([Part of upgrade 4.9-5.4]) - Object literal may only specify known properties, and 'label' does not exist in type '{ value: boolean | null; } | MultiValue<{ value: boolean | null; }>'.
				label: '',
				value,
			}}
			options={[
				{ label: formatMessage(messages.true), value: true },
				{ label: formatMessage(messages.false), value: false },
				{ label: formatMessage(messages.none), value: null },
			]}
			formatOptionLabel={formatOptionLabel}
			validationState={validationState}
			// eslint-disable-next-line @atlassian/eng-health/code-evolution/ts-migration-4.9-5.4
			// @ts-expect-error([Part of upgrade 4.9-5.4]) - Type '(change: OnChangeValue) => void' is not assignable to type '(newValue: SingleValue<{ value: boolean | null; }>, actionMeta: ActionMeta<{ value: boolean | null; }>) => void'.
			onChange={(change: OnChangeValue) => {
				if (change && change.label) {
					onChange(change);
				}
			}}
			classNamePrefix="insight-object-attribute-boolean-select"
			menuPortalTarget={selectInAModalStyleFixProps.menuPortalTarget}
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides -- Ignored via go/DSP-18766
			styles={{
				...selectInAModalStyleFixProps.styles,
				valueContainer: (base) => ({
					...base,
					// We need to adjust the left padding on the atlaskit parent, so that the value lines up with the read view
					paddingLeft: token('space.050'),
				}),
			}}
			descriptionId={props.ariaDescribedBy}
		/>
	);
};

BooleanSelect.defaultProps = {
	validationState: 'default',
};
